import { isEmpty } from '@/utils/general/browserHelper'
import { debounce } from '@/utils/general/browserHelper'
import { useRouter } from 'next/router'
import React, { RefObject, useEffect, useRef, useState } from 'react'
import { commonPayloadPartFf, getEmiDataPayloadFf } from '@/utils/srp/emi'
import { isFeatureEnabled } from '@/utils/general/uiHelper'
import { DataObjType, UuidMapType } from '@/utils/srp/types'
import { useAppDispatch, useAppSelector } from '@/redux/store/hooks'
import { fetchEmiEligibilityData, getEmiData, setEmiCalloutMsg, setEmiData } from '@/redux/slices/emiEligibility'
import { emiDataReqCount } from '@/constants/Srp'
import { SOMETHING_WENT_WRONG_ERROR } from '@/components/itinerary/Aside/CouponGvBlock/CouponGvConstants'

interface UseEmiProps {
  searchQuery: Record<string, string | string[] | undefined>
  sortedFilteredCards: any[]
  reactListItemsOneWay: RefObject<any>
  resultsData: Record<string, any>
  isPerAdult: boolean
  displayList: unknown[]
  showEmiPrice: boolean
  setShowEmiPrice: (value: boolean) => void
  pageScroll: HTMLDivElement | null
  toastHandler: (msg: string) => void
}

const useEmi = ({
  searchQuery = {},
  sortedFilteredCards = [],
  reactListItemsOneWay = { current: null },
  resultsData = {},
  isPerAdult = false,
  displayList = [],
  showEmiPrice = false,
  setShowEmiPrice = () => {},
  pageScroll = null,
  toastHandler,
}: UseEmiProps) => {
  const [fetchEmiFailed, setFetchEmiFailed] = useState(false)
  const dispatch = useAppDispatch()
  const router = useRouter()
  const displayListRef = useRef<unknown[]>([])
  displayListRef.current = displayList
  const { query = {} } = router || {}
  const [emiTooltipType, setEmiTooltipType] = useState({})
  const emiTooltipIndexRef = useRef({})
  emiTooltipIndexRef.current = emiTooltipType
  const [showEmiPopup, setShowEmiPopup] = useState({
    show: false,
    type: 'regular',
    fromBottomSheet: false,
  })
  const emiData = useAppSelector(getEmiData)
  const emiDataRef = useRef(emiData)
  emiDataRef.current = emiData
  const { noCostEmi = {} } = isFeatureEnabled() || {}

  useEffect(() => {
    if (showEmiPrice) {
      setShowEmiPrice(false)
      pageScroll?.removeEventListener('scroll', debouncedEmiData)
    }
    dispatch(setEmiData({}))
    dispatch(
      setEmiCalloutMsg({
        loading: false,
        loaded: false,
        data: {},
      })
    )
  }, [JSON.stringify(searchQuery)])

  useEffect(() => {
    if (showEmiPrice) {
      debouncedEmiData()
      pageScroll?.addEventListener('scroll', debouncedEmiData)
    } else {
      pageScroll?.removeEventListener('scroll', debouncedEmiData)
    }
    return () => {
      pageScroll?.removeEventListener('scroll', debouncedEmiData)
    }
  }, [showEmiPrice, sortedFilteredCards])

  useEffect(() => {
    if (emiData?.error) {
      toastHandler(SOMETHING_WENT_WRONG_ERROR)
      handleEmiFailure()
    }
  }, [emiData])

  const fetchEmiData = async () => {
    try {
      const finalDataObj: Record<string, DataObjType> = {}
      const finalUuidList: Record<string, UuidMapType[]> = {
        regular: [],
      }
      const totalCount = isEmpty(searchQuery?.returnDate) ? emiDataReqCount.oneWay : emiDataReqCount.roundTrip
      const { dataObj, flightReqData, uuidList } = getEmiDataPayloadFf(
        totalCount,
        reactListItemsOneWay?.current?.getVisibleRange()?.[0],
        displayListRef.current,
        emiDataRef.current,
        'regular',
        'REGULAR',
        resultsData
      )
      finalUuidList['regular'] = uuidList
      finalDataObj['regular'] = dataObj
      if (!isEmpty(flightReqData)) {
        const reqData = {
          ...commonPayloadPartFf(searchQuery, noCostEmi),
          sid: resultsData.searchId,
          flight_list: flightReqData,
        }
        dispatch(
          fetchEmiEligibilityData({
            payload: reqData,
            finalDataObj,
            finalUuidList,
            fareTypes: ['regular'],
            type: 'flightPrice',
          })
        )
        .unwrap()
      }
    } catch (e) {
      console.log(e)
      dispatch(setEmiData({ error: true }))
    }
  }

  const debouncedEmiData = debounce(fetchEmiData, 200)

  const handleEmiFailure = () => {
    setShowEmiPrice(false)
    setFetchEmiFailed(true)
    setTimeout(() => {
      dispatch(setEmiData({}))
      setFetchEmiFailed(false)
    }, 3000)
  }

  return {
    emiData,
    fetchEmiFailed,
  }
}

export default useEmi
